"use-strict";


window.theme = window.theme || {};

theme.config = {
  isExtraSmallQuery: false,
  isSmallQuery: false,
  isMediumQuery: false,
  isLargeQuery: false,
  isExtraLargeQuery: false,
  extraSmallQuery: "screen and (max-width: 575px)",
  smallQuery: "screen and (max-width: 767px)",
  mediumQuery: "screen and (max-width: 1024px)",
  largeQuery: "screen and (min-width: 1025px)",
  extraLargeQuery: "screen and (min-width: 1400px)"
};

theme.params = {
  isTouchDevice: (("ontouchstart" in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0)),
  isMotionReduced: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  isRtlDirection: document.documentElement.getAttribute("dir") === "rtl"
}

theme.helpers = {
  throttle: (callback) => {
    let requestId = null;
    let lastArgs;
    let context;

    const later = () => {
      requestId = null;
      if (lastArgs) {
        callback.apply(context, lastArgs);
        lastArgs = null;
      }
    };

    const throttled = function (...args) {
      context = this;
      lastArgs = args;

      if (requestId === null) {
        requestId = requestAnimationFrame(later);
      }
    };

    throttled.cancel = () => {
      if (requestId !== null) {
        cancelAnimationFrame(requestId);
        requestId = null;
      }
    };

    return throttled;
  },

  debounce: (fn, wait) => {
    let t;

    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  },

  capitalize: (str) => {
    return str.toLowerCase().split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  },

  safelyParseJson: (string) => {
    if (typeof string !== "string" || !string instanceof String) {
      return null;
    }

    try {
      JSON.parse(string);
    } catch (error) {
      console.error({ error: "Failed to parse JSON", string: string });

      return string;
    }

    return JSON.parse(string);
  },

  fetchConfig: (type = "json") => {
    return {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/${type}`,
      },
    }
  },

  cookiesEnabled: () => {
    let cookieEnabled = navigator.cookieEnabled;

    if (!cookieEnabled) {
      document.cookie = "testcookie";
      cookieEnabled = document.cookie.indexOf("testcookie") !== -1;
    }

    return cookieEnabled;
  },

  getCookie: (cname) => {
    let name = cname + "=";
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(";");

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];

      while (c.charAt(0) === " ") {
        c = c.substring(1);
      }

      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }

    return "";
  },

  deleteCookie: (cname) => {
    document.cookie = `${cname}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/;`;
  },

  setCookie: (cname, cvalue, exdays) => {
    const date = new Date();

    date.setTime(date.getTime() + exdays * 24 * 60 * 60 * 1000);

    let expires = "expires=" + date.toUTCString();

    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
  },

  pauseAllMedia: () => {
    const videos = document.querySelectorAll("lazy-video");

    videos.forEach((video) => {
      video.pause();
    });

    document.querySelectorAll("product-model").forEach((model) => {
      const modelButton = model.querySelector(".shopify-model-viewer-ui__button");

      if (modelButton) {
        modelButton.removeAttribute("hidden");
      }

      if (model.modelViewerUI) {
        model.modelViewerUI.pause();
      }
    });
  }
}

theme.accessibility = {
  trapFocusHandlers: {},

  getFocusableElements: (container) => {
    return Array.from(
      container.querySelectorAll(
        `summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe`
      )
    );
  },

  trapFocus: (container, elementToFocus = container) => {
    const elements = theme.accessibility.getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];

    theme.accessibility.removeTrapFocus();

    theme.accessibility.trapFocusHandlers.focusin = (event) => {
      if (event.target === container || event.target === last || event.target === first) {
        document.addEventListener("keydown", theme.accessibility.trapFocusHandlers.keydown);
      }
    };

    theme.accessibility.trapFocusHandlers.focusout = () => {
      document.removeEventListener("keydown", theme.accessibility.trapFocusHandlers.keydown);
    };

    theme.accessibility.trapFocusHandlers.keydown = (event) => {
      if (event.code.toUpperCase() === "TAB") {
        if (event.target === last && !event.shiftKey) {
          event.preventDefault();
          first.focus({ preventScroll: true });
        } else if ((event.target === container || event.target === first) && event.shiftKey) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        }
      }
    };

    document.addEventListener("focusout", theme.accessibility.trapFocusHandlers.focusout);
    document.addEventListener("focusin", theme.accessibility.trapFocusHandlers.focusin);

    elementToFocus.focus({ preventScroll: true });

    if (elementToFocus.tagName === "INPUT") {
      const selectableTypes = ["search", "text", "email", "url"];

      if (selectableTypes.includes(elementToFocus.type) && elementToFocus.value) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length);
      }
    }
  },

  removeTrapFocus: (elementToFocus = null) => {
    const { trapFocusHandlers } = theme.accessibility;

    document.removeEventListener("focusin", trapFocusHandlers.focusin);
    document.removeEventListener("focusout", trapFocusHandlers.focusout);
    document.removeEventListener("keydown", trapFocusHandlers.keydown);

    if (elementToFocus?.focus instanceof Function) {
      elementToFocus.focus({ preventScroll: true });
    }
  }
};

theme.assetsList = [];

theme.assets = {};

for (const asset of theme.assetsList) {
  const assetFiles = {};

  asset.files.forEach((assetFile) => {
    assetFiles[assetFile] = {
      loading: false,
      loaded: false,
    }
  });

  theme.assets[asset.name] = {
    ...assetFiles
  };
}

(function() {
  document.documentElement.classList.add(theme.params.isTouchDevice ? "touch" : "no-touch");

  const isExtraSmallQuery = window.matchMedia(theme.config.extraSmallQuery);
  const isSmallQuery = window.matchMedia(theme.config.smallQuery);
  const isMediumQuery = window.matchMedia(theme.config.mediumQuery);
  const isLargeQuery = window.matchMedia(theme.config.largeQuery);
  const isExtraLargeQuery = window.matchMedia(theme.config.extraLargeQuery);

  theme.config.isExtraSmallQuery = isExtraSmallQuery.matches;
  theme.config.isSmallQuery = isSmallQuery.matches;
  theme.config.isMediumQuery = isMediumQuery.matches;
  theme.config.isLargeQuery = isLargeQuery.matches;
  theme.config.isExtraLargeQuery = isExtraLargeQuery.matches;

  isExtraSmallQuery.onchange = (evt) => {
    theme.config.isExtraSmallQuery = evt.matches;

    document.dispatchEvent(new CustomEvent(theme.config.isExtraSmallQuery ? "breakpoint:xs:match" : "breakpoint:xs:unmatch"));
  }

  isSmallQuery.onchange = (evt) => {
    theme.config.isSmallQuery = evt.matches;

    document.dispatchEvent(new CustomEvent(theme.config.isSmallQuery ? "breakpoint:sm:match" : "breakpoint:sm:unmatch"));
  }

  isMediumQuery.onchange = (evt) => {
    theme.config.isMediumQuery = evt.matches;

    document.dispatchEvent(new CustomEvent(theme.config.isMediumQuery ? "breakpoint:md:match" : "breakpoint:md:unmatch"));
  }

  isLargeQuery.onchange = (evt) => {
    theme.config.isLargeQuery = evt.matches;

    document.dispatchEvent(new CustomEvent(theme.config.isLargeQuery ? "breakpoint:lg:match" : "breakpoint:lg:unmatch"));
  }

  isExtraLargeQuery.onchange = (evt) => {
    theme.config.isExtraLargeQuery = evt.matches;

    document.dispatchEvent(new CustomEvent(theme.config.isExtraLargeQuery ? "breakpoint:xl:match" : "breakpoint:xl:unmatch"));
  }
})();


function showToast(message, duration = 3000, customClass = "") {
  let toastContainer = document.getElementById("toast-container");

  if (!toastContainer) {
    toastContainer = document.createElement("div");
    toastContainer.id = "toast-container";

    document.body.appendChild(toastContainer);
  }

  const toast = document.createElement("div");

  toast.className = `toast show ${customClass}`;
  toast.innerHTML = message;

  toastContainer.appendChild(toast);

  setTimeout(() => {
    closeToast(toast);
  }, duration);
}


function closeToast(toast) {
  toast.classList.remove("show");

  setTimeout(() => {
    toast.remove();

    if (!document.querySelector("#toast-container .toast")) {
      document.getElementById("toast-container").remove();
    }
  }, 500);
}


class LazyMedia extends HTMLElement {
  constructor() {
    super();

    if (this.playPauseButton) {
      this.playPauseButton.addEventListener("click", this.handlePlayPauseButtonEvent.bind(this));
    }

    if (this.muteUnmuteButton) {
      this.muteUnmuteButton.addEventListener("click", this.handleMuteUnmuteButtonEvent.bind(this));
    }

    if (this.autoplay) {
      Motion.inView(this, () => {
        this.play();

        return () => {
          this.pause();
        };
      });
    }
  }

  get playPauseButton() {
    return this.querySelector('[id^="PlayPauseButton-"]');
  }

  get muteUnmuteButton() {
    return this.querySelector('[id^="MuteUnmuteButton-"]');
  }

  get autoplay() {
    return this.hasAttribute("autoplay");
  }

  get playing() {
    return this.hasAttribute("is-playing");
  }

  get player() {
    let playerObject = this.playerObject;

    if (!playerObject) {
      playerObject = new Proxy(this.videoPlayer(), {
        get: (object, property) => async () => {
          object = await object;

          this.handleVideoPlayer(object, property);
        }
      });
    }

    return playerObject;
  }

  handlePlayPauseButtonEvent(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.autoplay) {
      this.setAttribute("play-clicked", "");
    }

    if (this.playing) {
      this.paused = true;
      this.pause();
    } else {
      this.paused = false;
      this.play();
    }
  }

  handleMuteUnmuteButtonEvent(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.muted) {
      this.muted = false;
      this.unmute();
    } else {
      this.muted = true;
      this.mute();
    }
  }

  play() {
    if (!this.playing) {
      this.player.play();

      this.dispatchEvent(new CustomEvent("is-playing"));
    }
  }

  pause() {
    if (this.playing) {
      this.player.pause();
      this.dispatchEvent(new CustomEvent("is-paused"));
    }
  }

  mute() {
    this.player.mute();
    this.setAttribute("muted", "");
  }

  unmute() {
    this.player.unmute();
    this.removeAttribute("muted");
  }
}


class LazyVideo extends LazyMedia {
  constructor() {
    super();
  }

  videoPlayer() {
    if (this.hasAttribute("provider")) {
      this.setAttribute("loaded", "");
      this.closest(".media")?.classList.remove("loading");

      return new Promise(async (resolve) => {
        const iframe = this.querySelector("iframe");
        const iframeSrc = iframe?.getAttribute("data-src");

        if (iframeSrc) {
          iframe.setAttribute("src", iframeSrc);
          iframe.removeAttribute("data-src");
        }

        const isAutoplay = this.hasAttribute("autoplay");
        const muteVideo = isAutoplay || window.matchMedia("screen and (max-width: 1024.98px)").matches;

        this.muted = muteVideo;

        const script = document.createElement("script");

        script.type = "text/javascript";

        if (this.getAttribute("provider") === "youtube") {
          if (!window.YT || !window.YT.Player) {
            script.src = "https://www.youtube.com/iframe_api";

            document.head.appendChild(script);

            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }

          let player;

          if (window.YT && window.YT.Player) {
            player = new YT.Player(this.querySelector("iframe"), {
              events: {
                onReady: () => {
                  if (muteVideo) {
                    player.mute();
                    this.setAttribute("muted", "");
                  }

                  if (isAutoplay) {
                    this.setAttribute("is-playing", "");
                  }

                  resolve(player);
                },
                onStateChange: (event) => {
                  if (event.data === YT.PlayerState.PLAYING) {
                    this.setAttribute("is-playing", "");
                  } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                    this.removeAttribute("is-playing");
                  }
                }
              }
            });
          }

          const onYouTubePromise = new Promise((resolve) => {
            window.onYouTubeIframeAPIReady = () => resolve();
          });

          await onYouTubePromise;

          player = new YT.Player(this.querySelector("iframe"), {
            events: {
              onReady: () => {
                if (muteVideo) {
                  player.mute();
                  this.setAttribute("muted", "");
                }

                if (isAutoplay) {
                  this.setAttribute("is-playing", "");
                }

                resolve(player);
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute("is-playing", "");
                } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                  this.removeAttribute("is-playing");
                }
              }
            }
          });
        }

        if (this.getAttribute("provider") === "vimeo") {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = "https://player.vimeo.com/api/player.js";

            document.head.appendChild(script);

            await new Promise(resolve2 => {
              script.onload = resolve2;
            });
          }

          const player = new Vimeo.Player(this.querySelector("iframe"));

          if (muteVideo) {
            player.setMuted(true);
            this.setAttribute("muted", "");
          }

          player.on("play", () => {
            this.setAttribute("is-playing", "");
          });

          player.on("pause", () => {
            this.removeAttribute("is-playing")
          });

          player.on("ended", () => {
            this.removeAttribute("is-playing")
          });

          resolve(player);
        }
      });
    } else {
      const video = this.querySelector("video");
      const videoSrc = video?.getAttribute("data-src");
      const isAutoplay = this.hasAttribute("autoplay");

      if (videoSrc) {
        video.setAttribute("src", videoSrc);
        video.removeAttribute("data-src");
      }

      this.muted = isAutoplay;

      if (isAutoplay) {
        video.muted = true;

        this.setAttribute("muted", "");
      }

      this.setAttribute("loaded", "");
      this.closest(".media")?.classList.remove("loading");

      video.addEventListener("play", () => {
        this.setAttribute("is-playing", "");
        this.removeAttribute("suspended");
      });

      video.addEventListener("pause", () => {
        if (!video.seeking && video.paused) {
          this.removeAttribute("is-playing");
        }
      });

      return video;
    }
  }

  handleVideoPlayer(object, property) {
    const provider = this.getAttribute("provider");

    if (provider === "youtube") {
      switch (property) {
        case "play":
          object.playVideo();

          break;

        case "pause":
          object.pauseVideo();

          break;

        case "mute":
          object.mute();

          break;

        case "unmute":
          object.unMute();

          break;
      }
    } else if (provider === "vimeo") {
      switch (property) {
        case "mute":
          object.setMuted(true);

          break;

        case "unmute":
          object.setMuted(false);

          break;

        default:
          object[property]();

          break;
      }
    } else {
      if (property === "play") {
        object.play();
      } else if (property === "mute") {
        object.muted = true;
      } else if (property === "unmute") {
        object.muted = false;
      } else {
        object.pause();
      }
    }
  }
}

if (!window.customElements.get("lazy-video")) {
  customElements.define("lazy-video", LazyVideo);
}


class ModalBase extends HTMLElement {
  constructor() {
    super();

    this.isBusy = false;
  }

  get closeIcon() {
    return `
      <span class="button__flair"></span>

      <span class="button__inner">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 12.82 12.28">
          <path d="M12.82 12.16 8.34 7.91M0 0l4.56 4.32" stroke="currentColor" style="stroke-dashoffset: 0; stroke-dasharray: 6.2283px, 6.3283px;"></path>
          <path d="M.34 12.28 12.54.22" stroke="currentColor" style="stroke-dashoffset: 0; stroke-dasharray: none;"></path>
        </svg>
      </span>
    `;
  }

  get isOpen() {
    return this.modal?.classList.contains("modal--visible");
  }

  get hasOpenDrawerAfterModal() {
    let nextElement = this.modal.nextElementSibling;

    while (nextElement) {
      if (nextElement.querySelector(".drawer.open")) {
        return true;
      }

      nextElement = nextElement.nextElementSibling;
    }

    return false;
  }

  get hasOpenModalAfterThis() {
    const allModals = Array.from(document.querySelectorAll(".modal.modal--visible"));
    const modalsAfter = allModals.filter((modal) => {
      return this.modal !== modal && this.compareDocumentPosition(modal) & Node.DOCUMENT_POSITION_FOLLOWING;
    });

    return modalsAfter.length > 0;
  }

  get focusElement() {
    return this.modal.querySelector("button");
  }

  init(options) {
    if (this.modal) {
      return;
    }

    this.defaults = {
      id: "",
      customClass: "",
      headerTitle: "",
      content: "",
      closeLabel: "Close",
      closeMethods: ["overlay", "button", "escape"],
      transitionDuration: 100,
    };

    this.opts = Object.assign({}, this.defaults, options);

    this.build();
    this.bindEvents();

    document.body.insertAdjacentElement("beforeend", this.modal);

    document.dispatchEvent(new CustomEvent("hover-button:apply"));
  }

  destroy() {
    if (this.modal === null) {
      return;
    }

    if (this.isOpen) {
      this.close();
    }

    this.unbindEvents();

    this.modal.remove();

    this.modal = null;
  }

  open(activeElement = null) {
    if (this.isBusy) {
      return;
    }

    this.isBusy = true;

    this.activeElement = activeElement;

    if (this.modal.style.removeProperty) {
      this.modal.style.removeProperty("display");
    } else {
      this.modal.style.removeAttribute("display");
    }

    document.getSelection().removeAllRanges();

    setTimeout(() => {
      this.modal.classList.add("modal--visible");
    }, 10);


    setTimeout(() => {
      theme.accessibility.trapFocus(this.modal, this.focusElement);
    }, this.opts.transitionDuration);

    window.bodyScrollLock.disableBodyScroll(this.modal.querySelector(".modal__content") || this.modal);

    document.documentElement.classList.add("page-no-scroll");

    const visibleDrawer = document.querySelector(".drawer.open");

    if (visibleDrawer) {
      this.modal.classList.add("modal--drawer-visible");
    }

    this.isBusy = false;
  }

  close() {
    if (this.isBusy) {
      return;
    }

    this.isBusy = true;

    this.modal.classList.add("modal--is-hiding");
    this.modal.classList.remove("modal--visible");

    const visibleModalsOrDrawers = document.querySelector(".modal.modal--visible, .drawer.open");

    if (!visibleModalsOrDrawers) {
      window.bodyScrollLock.clearAllBodyScrollLocks();

      document.documentElement.classList.remove("page-no-scroll");
    }

    if (this.isOpen) {
      this.close();
    }

    this.unbindEvents();

    setTimeout(() => {
      this.modal.classList.remove("modal--is-hiding");

      this.modal?.remove();
      this.modal = null;

      theme.accessibility.removeTrapFocus(this.activeElement);
    }, 600);

    setTimeout(() => {
      theme.accessibility.removeTrapFocus(this.activeElement);
    }, 50);

    this.isBusy = false;
  }

  insertModalHeader() {
    return `
      ${ (this.opts.closeMethods.includes("button") && !this.opts.headerTitle && this.opts.closeButtonPosition !== "content") ? `
        <button type="button" class="modal__close button button-reset hover:scale[1.15]">
          ${ this.closeIcon }
          
          <span class="visually-hidden">${ this.opts.closeLabel }</span>
        </button>
      ` : '' }

      <div class="modal__header bg-gradient">
        ${ this.opts.headerTitle ? `
          <h4 class="h5 m-0 flex-1 uppercase">${ this.opts.headerTitle }</h4>
        ` : '' }
        
        ${ (this.opts.closeMethods.includes("button") && this.opts.closeButtonPosition !== "content") ? `
          <button type="button" class="modal__close button button-reset hover:scale[1.15]">
            ${ this.closeIcon }
            
            <span class="visually-hidden">${ this.opts.closeLabel }</span>
          </button>
        ` : '' }
      </div>
    `;
  }

  insertModalContent() {
    const isDomContent = typeof this.opts.content !== "string";
    const customClass = isDomContent ? this.opts.content.dataset.customClass : "";
    const contentElement = isDomContent ? this.opts.content.cloneNode(true) : null;

    if (this.opts.closeButtonPosition === "content" && this.opts.closeButtonContainerSelector && contentElement) {
      const container = contentElement.content.querySelector(this.opts.closeButtonContainerSelector);

      if (container) {
        container.insertAdjacentHTML("beforeend", `
          <button type="button" class="modal__close button button-reset hover:scale[1.15]">
            ${this.closeIcon}
            <span class="visually-hidden">${this.opts.closeLabel}</span>
          </button>
        `);
      }
    }

    return `
      <div class="modal__content ${customClass || ''}">
        ${ this.opts.content ? typeof this.opts.content === "string" ? this.opts.content : contentElement.innerHTML : "" }
      </div>
    `;
  }

  build() {
    this.modal = document.createElement("div");

    this.modal.classList.add("modal");
    this.modal.setAttribute("role", "dialog");
    this.modal.setAttribute("aria-modal", "true");

    if (this.opts.headerTitle) {
      this.modal.setAttribute("aria-label", this.opts.headerTitle);
    }

    if (this.opts.id) {
      this.modal.id = this.opts.id;
    }

    if (this.opts.customClass) {
      this.modal.className += ` ${this.opts.customClass}`;
    }

    this.modal.style.display = "none";

    const markup = `
      <div class="modal__box">
        ${ this.insertModalHeader() }
        ${ this.insertModalContent() }
      </div>
      
      <overlay-component class="overlay overlay--modal"></overlay-component>
    `;

    const fragment = this.createFragmentFromMarkup(markup);

    this.modal.appendChild(fragment);

    this.modalCloseBtn = this.modal.querySelector(".modal__close");
    this.overlay = this.modal.querySelector("overlay-component");

    const modalImages = this.modal.querySelectorAll(".fade-in-image");

    modalImages.forEach((image) => {
      image.classList.remove("fade-in-image");
    });
  }

  createFragmentFromMarkup(markup) {
    const fragment = document.createDocumentFragment();
    const tempContainer = document.createElement("div");

    tempContainer.innerHTML = markup;

    while (tempContainer.firstChild) {
      fragment.appendChild(tempContainer.firstChild);
    }

    return fragment;
  }

  bindEvents() {
    this.events = {
      clickCloseBtn: this.close.bind(this),
      clickOverlay: this.handleClickOutside.bind(this),
      escClick: this.handleEscClick.bind(this)
    };

    if (this.opts.closeMethods.includes("button")) {
      this.modalCloseBtn?.addEventListener("click", this.events.clickCloseBtn);
      this.overlay.addEventListener("click", this.events.clickCloseBtn);
    }

    this.modal.addEventListener("mousedown", this.events.clickOverlay);
    document.addEventListener("keydown", this.events.escClick);
  }

  handleEscClick(evt) {
    if (this.opts.closeMethods.includes("escape") && (evt.key === "Escape" || evt.key === "Esc") && this.isOpen && !this.hasOpenDrawerAfterModal && !this.hasOpenModalAfterThis) {
      this.close();
    }
  }

  handleClickOutside(event) {
    const scrollbarWidth = this.modal.offsetWidth - this.modal.clientWidth;
    const clickedOnScrollbar = event.clientX >= this.modal.offsetWidth - 15;
    const isScrollable = this.modal.scrollHeight !== this.modal.offsetHeight;

    if (navigator.platform === "MacIntel" && scrollbarWidth === 0 && clickedOnScrollbar && isScrollable) {
      return;
    }

    if (this.opts.closeMethods.includes("overlay") && !this.findAncestor(event.target, "modal") && event.clientX < this.modal.clientWidth) {
      this.close();
    }
  }

  findAncestor(el, cls) {
    while (el && (el = el.parentElement)) {
      if (el.classList.contains(cls)) {
        return el;
      }
    }

    return null;
  }

  unbindEvents() {
    if (this.opts.closeMethods.includes("button")) {
      this.modalCloseBtn?.removeEventListener("click", this.events.clickCloseBtn);
      this.overlay.removeEventListener("click", this.events.clickCloseBtn);
    }

    this.modal.removeEventListener("mousedown", this.events.clickOverlay);
  }
}


class Drawer extends HTMLElement {
  constructor() {
    super();

    this.closeButtons = this.querySelectorAll("[data-close]:not(.drawer--inner :is([data-close]))");
    this.position = this.classList.contains("drawer--right") ? "right" : "left";

    this.closeButtons.forEach((button) => {
      button.addEventListener("click", this.close.bind(this));
    });

    document.addEventListener("drawer:update-close-buttons", () => {
      this.closeButtons = this.querySelectorAll("[data-close]:not(.drawer--inner :is([data-close]))");

      this.closeButtons.forEach((button) => {
        button.removeEventListener("click", this.close.bind(this));
        button.addEventListener("click", this.close.bind(this));
      });
    });

    this.onOpenTransitionEnd = this.handleOpenTransitionEnd.bind(this);
    this.onCloseTransitionEnd = this.handleCloseTransitionEnd.bind(this);

    this.onEscClick = this.handleEscClick.bind(this);
    this.onOverlayClick = this.handleOverlayClick.bind(this);

    document.addEventListener("shopify:section:select", this.openDrawerInCustomizer.bind(this));
    document.addEventListener("shopify:section:deselect", this.closeDrawerInCustomizer.bind(this));
    document.addEventListener("shopify:section:load", this.openDrawerInCustomizer.bind(this));
  }

  get overlay() {
    return this.nextElementSibling?.classList.contains("overlay") ? this.nextElementSibling : null;
  }

  get focusElement() {
    return this.querySelector("button");
  }

  get hasOpenModalAfterDrawer() {
    const visibleModal = document.querySelector(".modal.modal--visible, .modal.modal--is-hiding");

    if (visibleModal) {
      return visibleModal.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_PRECEDING;
    }

    return false;
  }

  openDrawerInCustomizer(e) {
    const isToOpenDrawer = ["CartDrawer", "SearchDrawer"].includes(this.id);

    if (!isToOpenDrawer) {
      return;
    }

    const sectionId = e.detail.sectionId;
    const drawer = document.getElementById(`shopify-section-${sectionId}`)?.querySelector(`#${this.id}`);

    if (drawer) {
      if (e.type === "shopify:section:select") {
        this.open();
      } else {
        drawer.classList.add("open");
        drawer.classList.remove("closed");
      }
    }
  }

  closeDrawerInCustomizer(e) {
    const sectionId = e.detail.sectionId;
    const drawerId = this.id || this.dataset.id;

    if (!drawerId) {
      return;
    }

    const drawer = document.getElementById(`shopify-section-${sectionId}`)?.querySelector(`#${drawerId}`);

    if (drawer) {
      this.close();
    }
  }

  handleOpenTransitionEnd(event) {
    if (event.target.id !== this.id) {
      return;
    }

    theme.accessibility.trapFocus(this, this.focusElement);
  }

  handleCloseTransitionEnd(event) {
    if (event.target.id !== this.id) {
      return;
    }

    this.classList.add("!hidden");

    theme.accessibility.removeTrapFocus(this.activeElement);
  }

  open(activeElement = null) {
    this.triggerButtons = document.querySelectorAll(`[is="drawer-trigger"][aria-controls='${this.id || ''}']`);
    this.activeElement = activeElement;

    const openedDrawer = document.querySelector(".drawer:not(.drawer--inner).open");

    if (openedDrawer && !this.classList.contains("drawer--inner")) {
      openedDrawer.close();
    }

    this.classList.remove("!hidden");

    requestAnimationFrame(() => {
      this.overlay.style.opacity = "";

      this.classList.add("open");

      window.bodyScrollLock.disableBodyScroll(this, {
        reserveScrollBarGap: true,
        allowTouchMove: (element) => {
          const scrollingContainer = element.closest(".allow-scrolling");

          return scrollingContainer && scrollingContainer.scrollHeight > scrollingContainer.clientHeight;
        }
      });

      if (!this.classList.contains("drawer--inner")) {
        document.documentElement.classList.add("page-no-scroll", `drawer-${this.position}-opened`);
      }

      this.removeEventListener("transitionend", this.onCloseTransitionEnd, false);
      this.addEventListener("transitionend", this.onOpenTransitionEnd, false);

      this.overlay?.addEventListener("click", this.onOverlayClick);
      document.addEventListener("keydown", this.onEscClick);

      this.triggerButtons.forEach((button) => {
        button.setAttribute("aria-expanded", "true");
      });

      this.dispatchEvent(new CustomEvent("drawer:open"));
    });
  }

  close() {
    this.getAnimations().forEach(anim => anim.cancel());
    this.overlay.getAnimations().forEach(anim => anim.cancel());

    this.triggerButtons = document.querySelectorAll(`[is="drawer-trigger"][aria-controls='${this.id || ''}']`);

    const openedInnerDrawers = this.querySelectorAll(".drawer--inner.open");

    openedInnerDrawers.forEach((innerDrawer) => {
      innerDrawer.close();
    });

    requestAnimationFrame(() => {
      this.classList.remove("open");

      const hasOpenedModal = document.querySelector(".modal.modal--visible");
      const hasOpenedDrawer = document.querySelector(".drawer.open");

      if (!hasOpenedModal && !hasOpenedDrawer) {
        window.bodyScrollLock.clearAllBodyScrollLocks();

        document.documentElement.classList.remove("page-no-scroll");
      }

      if (!hasOpenedDrawer && !this.classList.contains("drawer--inner")) {
        document.documentElement.classList.remove("drawer-left-opened", "drawer-right-opened");
      }
    });

    this.removeEventListener("transitionend", this.onOpenTransitionEnd, false);
    this.addEventListener("transitionend", this.onCloseTransitionEnd, false);

    this.overlay?.removeEventListener("click", this.onOverlayClick);
    document.removeEventListener("keydown", this.onEscClick);

    this.triggerButtons.forEach((button) => {
      button.setAttribute("aria-expanded", "false");
    });

    this.dispatchEvent(new CustomEvent("drawer:close"));
  }

  handleEscClick(evt) {
    const openedInnerDrawer = this.querySelector("drawer-inner.open");

    if (!this.hasOpenModalAfterDrawer && (evt.key === "Escape" || evt.key === "Esc")) {
      if (openedInnerDrawer) {
        openedInnerDrawer.close();
      } else {
        this.close();
      }
    }
  }

  handleOverlayClick() {
    this.close();
  }
}


class DrawerInner extends Drawer {
  constructor() {
    super();

    this.closeButtons = this.querySelectorAll("[data-close]");

    this.closeButtons.forEach((button) => {
      button.addEventListener("click", this.close.bind(this));
    });

    document.addEventListener("drawer:update-close-buttons", () => {
      this.closeButtons = this.querySelectorAll("[data-close]");

      this.closeButtons.forEach((button) => {
        button.removeEventListener("click", this.close.bind(this));
        button.addEventListener("click", this.close.bind(this));
      });
    });
  }
}

if (!window.customElements.get("drawer-inner")) {
  customElements.define("drawer-inner", DrawerInner);
}


class SlideshowSection extends HTMLElement {
  constructor() {
    super();

    this.globalSlide = null;
    this.thumbnailSlide = null;

    this.handleScrollListener = theme.helpers.throttle(this.handleScroll.bind(this));
    this.handleResizeListener = theme.helpers.throttle(this.handleResize.bind(this));
    this.handleBlockSelectListener = this.handleBlockSelect.bind(this);
    this.handleBlockDeselectListener = this.handleBlockDeselect.bind(this);

    this.init();
  }

  connectedCallback() {
    window.addEventListener("scroll", this.handleScrollListener, { passive: true });
    window.addEventListener("resize", this.handleResizeListener, { passive: true });

    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", this.handleBlockSelectListener);
      this.addEventListener("shopify:block:deselect", this.handleBlockDeselectListener);
    }
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.handleScrollListener);
    window.removeEventListener("resize", this.handleResizeListener);

    if (Shopify.designMode) {
      this.removeEventListener("shopify:block:select", this.handleBlockSelectListener);
      this.removeEventListener("shopify:block:deselect", this.handleBlockDeselectListener);
    }
  }

  handleScroll() {
    this.resizeSlider();
  }

  handleResize() {
    this.resizeSlider(true);
  }

  init() {
    if (document.body.classList.contains("index")) {
      const pos = window.pageYOffset;

      if (pos > 0 || document.body.classList.contains("swiper-lazy")) {
        this.initSlide();
      } else {
        if (this.classList.contains("lazy-loading-swiper-before")) {
          this.initSlide();
        } else {
          this.classList.add("lazy-loading-swiper-after");
        }
      }
    } else {
      this.initSlide();
    }

    this.resizeSlider();
  }

  initSlide() {
    const settings = theme.helpers.safelyParseJson(this.getAttribute("settings")) || {};

    const defaultSettings = {
      autoplay: false,
      loop: false,
      itemDesktop: 4,
      itemTablet: 1,
      itemMobile: 1,
      itemExtraMobile: 1,
      directionOnMobile: "horizontal",
      directionOnTablet: "horizontal",
      directionOnDesktop: "horizontal",
      autoplaySpeed: 3000,
      speed: 400,
      effect: "slide",
      crossFade: false,
      row: 1,
      spacingOnMobile: 0,
      spacingOnDesktop: 0,
      paginationProgressbar: false,
      autoItem: false,
      arrowCenterImage: 0,
      pauseOnMouseEnter: false,
      setMaxHeight: true,
      centeredSlides: false,
      grabCursor: false,
      autoHeight: false,
      roundLengths: false,
      loopAdditionalSlides: 0,
      loopAddBlankSlides: true,
      slidesPerGroup: 1,
      allowTouchMove: true,
    };

    this.settings = {...defaultSettings, ...settings};

    if (!settings.itemExtraMobile) {
      this.settings.itemExtraMobile = this.settings.itemMobile;
    }

    if (this.settings.autoplay) {
      this.settings.autoplay = {
        delay: this.settings.autoplaySpeed,
        pauseOnMouseEnter: this.settings.pauseOnMouseEnter,
      };
    }

    if (!this.settings.itemTablet) {
      if (this.settings.itemDesktop < 2) {
        this.settings.itemTablet = 1;
      } else if (this.settings.itemDesktop < 3) {
        this.settings.itemTablet = 2;
      } else {
        this.settings.itemTablet = 3;
      }
    }

    if (this.settings.direction === "vertical" && this.setMaxHeight) {
      this.style.maxHeight = this.offsetHeight + "px";
    }

    this.globalSlide = new Swiper(this, {
      observeParents: true,

      // Layout base (mobile)
      slidesPerView: this.settings.autoItem ? "auto" : this.settings.itemExtraMobile,
      spaceBetween: this.settings.spacingOnMobile,
      direction: this.settings.directionOnMobile,
      grid: { rows: this.settings.row, fill: "row" },

      // Comportamento
      autoplay: this.settings.autoplay,
      loop: this.settings.loop,
      allowTouchMove: this.settings.allowTouchMove,
      centeredSlides: this.settings.centeredSlides,
      roundLengths: this.settings.roundLengths,
      autoHeight: this.settings.autoHeight,
      touchStartPreventDefault: false,
      passiveListeners: true,
      slidesPerGroup: Math.max(1, this.settings.slidesPerGroup || 1),
      loopAdditionalSlides: this.settings.loopAdditionalSlides,
      loopAddBlankSlides: this.settings.loopAddBlankSlides,

      // Effetto
      effect: (this.settings.effect === "fade" ? "slide" : this.settings.effect),
      fadeEffect: { crossFade: this.settings.crossFade },

      // 💨 Reattività/velocità
      speed: (typeof this.settings.speed === "number" ? this.settings.speed : 20),
      followFinger: true,
      touchRatio: 1.3,
      resistanceRatio: 0.25,
      threshold: 4,
      longSwipesMs: 120,
      longSwipesRatio: 0.15,
      shortSwipes: true,

      // Navigazione/Paginazione
      navigation: {
        nextEl: this.querySelector(".swiper-button-next"),
        prevEl: this.querySelector(".swiper-button-prev"),
      },
      pagination: {
        clickable: true,
        el: this.querySelector(".swiper-pagination"),
        type: this.settings.paginationProgressbar ? "progressbar" : "bullets",
      },

      // Thumbs (se presenti)
      thumbs: {
        swiper: this.thumbnailSlide ? this.thumbnailSlide : null,
      },

      // Breakpoints “scattanti”
      breakpoints: {
        475: {
          slidesPerView: this.settings.itemMobile,
          speed: (typeof this.settings.speed === "number" ? this.settings.speed : 220),
          touchRatio: 1.3,
          resistanceRatio: 0.25,
          longSwipesMs: 120,
          longSwipesRatio: 0.15,
        },
        768: {
          direction: this.settings.directionOnTablet,
          slidesPerView: this.settings.itemTablet,
          spaceBetween: this.settings.spacingOnDesktop,
          speed: (typeof this.settings.speed === "number" ? this.settings.speed : 220),
          touchRatio: 1.3,
          resistanceRatio: 0.25,
          longSwipesMs: 120,
          longSwipesRatio: 0.15,
        },
        1025: {
          direction: this.settings.directionOnDesktop,
          slidesPerView: this.settings.itemDesktop,
          spaceBetween: this.settings.spacingOnDesktop,
          speed: (typeof this.settings.speed === "number" ? this.settings.speed : 220),
          touchRatio: 1.3,
          resistanceRatio: 0.25,
          longSwipesMs: 120,
          longSwipesRatio: 0.15,
        },
      },

      // Eventi
      on: {
        init: (e) => {
          if (this.settings.arrowCenterImage) {
            const slides = this.querySelectorAll(".product-item__media--ratio");
            if (slides.length > 0) {
              const heights = [];
              slides.forEach((slide) => heights.push(slide.offsetHeight / 2));
              const maxHeight = Math.max(...heights);
              const arrowsOffset = `--arrows-offset-top: ${maxHeight}px`;
              const arrows = this.querySelectorAll(".swiper-arrow");
              arrows.forEach((arrow) => arrow.setAttribute("style", arrowsOffset));
            }
          }

          const video = this.querySelector("video.slideshow");
          if (video) {
            const handleIntersection = (entries, observer) => {
              if (!entries[0].isIntersecting) return;
              observer.unobserve(this);

              const videos = this.querySelectorAll("video");

              const updateVideoPoster = () => {
                videos.forEach((video) => {
                  const posterDesktop = video.dataset.posterDesktop;
                  const posterMobile = video.dataset.postermobile;
                  if (posterDesktop && posterMobile) {
                    video.poster = theme.config.isSmallQuery ? `${posterMobile}` : `${posterDesktop}`;
                  }
                });
              };

              document.addEventListener("breakpoint:sm:match", updateVideoPoster);
              document.addEventListener("breakpoint:sm:unmatch", updateVideoPoster);

              videos.forEach((video) => {
                const src = video.dataset.src;
                const posterDesktop = video.dataset.posterDesktop;
                const posterMobile = video.dataset.posterMobile;

                if (src) {
                  video.src = src;
                  if (posterDesktop && posterMobile) {
                    video.poster = theme.config.isSmallQuery ? `${posterMobile}` : `${posterDesktop}`;
                  }
                  video.removeAttribute("data-src");
                }
              });
            };

            new IntersectionObserver(handleIntersection.bind(this), {
              rootMargin: "0px 0px 200px 0px",
            }).observe(this);

            this.dispatchEvent(new CustomEvent("carousel:init", { detail: { event: e } }));
          }
        },

        beforeTransitionStart: (e) => {
          const animatedLists = this.querySelectorAll(".swiper-slide:not(.swiper-slide-active) animated-list");
          const accentTexts = this.querySelectorAll(".swiper-slide:not(.swiper-slide-active) .accent-text");
          animatedLists.forEach((list) => list.classList.remove("visible"));
          accentTexts.forEach((text) => text.classList.remove("animate"));

          this.dispatchEvent(new CustomEvent("carousel:beforeTransitionStart", { detail: { event: e } }));
        },

        slideChangeTransitionEnd: (e) => {
          this.resizeSlider();
          const accentTexts = this.querySelectorAll(".swiper-slide.swiper-slide-active .accent-text");
          accentTexts.forEach((text) => {
            Motion.inView(text, () => text.classList.add("animate"));
          });

          this.dispatchEvent(new CustomEvent("carousel:transitionStart", { detail: { event: e } }));
        },
      },
    });

  }

  resizeSlider(updateSlide) {
    const currentSlide = this.querySelector(".swiper-slide-active .slideshow-slide");

    if (!currentSlide) {
      return;
    }

    const newHeight = currentSlide.offsetHeight;

    if (this.style.maxHeight !== `${newHeight}px`) {
      this.style.maxHeight = `${newHeight}px`;
    }

    if (updateSlide) {
      this.globalSlide.update();
    }
  }

  handleBlockSelect(e) {
    const index = Array.from(e.target.parentNode.children).indexOf(e.target);
    const slideshow = e.target.closest("slideshow-section");

    slideshow?.globalSlide.slideTo(index);
    slideshow?.globalSlide.autoplay?.pause();
  }

  handleBlockDeselect(e) {
    const slideshow = e.target.closest("slideshow-section");

    slideshow?.globalSlide.autoplay?.resume();
  }
}

if (!window.customElements.get("slideshow-section")) {
  customElements.define("slideshow-section", SlideshowSection);
}


class ProductRecommendations extends SlideshowSection {
  constructor() {
    super();

    this.type = this.getAttribute("type") || "auto";

    this.init();

    document.addEventListener("shopify:section:load", this.init.bind(this));
  }

  init() {
    if (!this.type) {
      return;
    }

    if (this.type === "manual") {
      this.initSlide();

      return;
    }

    this.settings = theme.helpers.safelyParseJson(this.getAttribute("settings")) || {};

    this.recommendationsUrl = this.settings?.recommendationsUrl || "";
    this.location = this.settings?.location || "product";
    this.wrapper = this.closest(".cart-recommendations");

    if (this.location === "cart") {
      this.parseProducts();
    } else {
      Motion.inView(this.closest(".section, .quickview"), () => {
        this.parseProducts();
      });
    }
  }

  parseProducts() {
    if (!this.recommendationsUrl) {
      return;
    }

    fetch(this.recommendationsUrl)
      .then((response) => response.text())
      .then((text) => {
        const parsedHTML = new DOMParser().parseFromString(text, "text/html");
        const recommendations = parsedHTML.querySelector("product-recommendations");
        const parsedProductWrapper = recommendations?.querySelector(".swiper-wrapper, .recommended-blocks");
        const parsedProducts = parsedProductWrapper?.querySelectorAll(".swiper-slide, .grid-custom-item");
        const currentProductWrapper = this.querySelector(".swiper-wrapper");
        const currentArrows = this.querySelector(".cart-recommendations__arrows, .product-recommendations__arrows");

        if (parsedProducts && parsedProducts.length <= 1) {
          currentArrows?.classList.add("hidden");
        } else {
          currentArrows?.classList.remove("hidden");
        }

        if (!parsedProductWrapper || (!parsedProducts || !parsedProducts.length)) {
          if (this.location === "product") {
            const sectionWrapper = this.closest(".product-recommendations-section");

            if (sectionWrapper) {
              sectionWrapper.remove();
            } else {
              this.remove();
            }

            document.dispatchEvent(new CustomEvent("theme:main-roundness:update"));
          } else if (this.location === "cart") {
            this.wrapper.classList.add("!hidden");
          }
        } else if (recommendations) {
          if (this.location === "product") {
            this.innerHTML = recommendations.innerHTML;

            this.classList.remove("!hidden");
          } else if (this.location === "cart") {
            currentProductWrapper.innerHTML = parsedProductWrapper.innerHTML;

            this.wrapper.classList.remove("!hidden");
          }

          document.dispatchEvent(new CustomEvent("hover-button:apply"));
        }
      })
      .finally(() => {
        if (this.location === "cart" && this.querySelector(".swiper-slide")) {
          this.initSlide();
        }

        if (this.location === "product" && this.querySelector(".swiper-slide")) {
          this.initSlide();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

if (!window.customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}


class AnimatedList extends HTMLElement {
  constructor() {
    super();

    this.initialDelay = 0;
    this.delayIncrement = 0.15;
    this.animateOnce = true;
    this.setBlocksDelay = true;
    /* A relative threshold (fraction of the element's OWN total area) breaks
       tall lists: a single-column mobile grid with many stacked items can be
       thousands of px tall, so "10% of it visible" needs far more scroll than
       normal before the reveal ever fires — the whole list (all items are
       hidden together, not per-item) looks empty until that's met. 0 fires as
       soon as any pixel of the list is visible, independent of its height. */
    this.threshold = 0;

    if (!this.isAnimatable) {
      return;
    }

    this.blocks = this.querySelectorAll(".animated-block");

    this.blocks = Array.from(this.blocks).filter((block) => {
      let current = block.parentElement;

      while (current && current !== this) {
        if (current.classList.contains("animated-blocks")) {
          return false;
        }

        current = current.parentElement;
      }

      return true;
    });

    this.handleSectionLoadListener = this.init.bind(this);
  }

  get isAnimatable() {
    return document.body.classList.contains("animate-on-scroll");
  }

  connectedCallback() {
    this.init();

    if (Shopify.designMode) {
      document.addEventListener("shopify:section:load", this.handleSectionLoadListener);
    }
  }

  disconnectedCallback() {
    if (Shopify.designMode) {
      document.removeEventListener("shopify:section:load", this.handleSectionLoadListener);
    }
  }

  init() {
    const settings = theme.helpers.safelyParseJson(this.getAttribute("settings"));

    if (settings) {
      const validKeys = [
        "initialDelay", "delayIncrement", "animateOnce", "animate", "setBlocksDelay", "threshold"
      ];

      for (const [key, value] of Object.entries(settings)) {
        if (validKeys.includes(key)) {
          this[key] = value;
        }
      }
    }
    
    // code added by benitronica for aditional control
    if (this.setBlocksDelay && Array.isArray(this.blocks) || (this.blocks && typeof this.blocks.forEach === 'function')) {
      let delay = this.initialDelay;
    
      this.blocks.forEach((block) => {
        block.style.transitionDelay = `${delay}s`;
        delay += this.delayIncrement;
      });
    }

    // if (this.setBlocksDelay) {
    //   let delay = this.initialDelay;

    //   this.blocks.forEach((block) => {
    //     block.style.transitionDelay = `${delay}s`;

    //     delay += this.delayIncrement;
    //   });
    // }

    const handleIntersection = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.classList.add("visible");
        } else {
          if (!this.animateOnce) {
            this.classList.remove("visible");
          }
        }
      });

      this.dispatchEvent(new CustomEvent("animated-list:is-visible"));
    }

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: "100px 0px 0px 0px",
      threshold: this.threshold
    });

    observer.observe(this);
  }
}

if (!window.customElements.get("animated-list")) {
  customElements.define("animated-list", AnimatedList);
}


class AnimatedMedia extends HTMLElement {
  constructor() {
    super();

    this.initialDelay = 0;
    this.delayIncrement = 0.1;

    this.blocks = this.querySelectorAll(".animated-block");

    this.handleSectionLoadListener = this.init.bind(this);
  }

  connectedCallback() {
    this.init();

    if (Shopify.designMode) {
      document.addEventListener("shopify:section:load", this.handleSectionLoadListener);
    }
  }

  disconnectedCallback() {
    if (Shopify.designMode) {
      document.removeEventListener("shopify:section:load", this.handleSectionLoadListener);
    }
  }

  init() {
    const settings = theme.helpers.safelyParseJson(this.getAttribute("settings"));

    if (settings) {
      const validKeys = [
        "initialDelay", "delayIncrement", "animateOnce"
      ];

      for (const [key, value] of Object.entries(settings)) {
        if (validKeys.includes(key)) {
          this[key] = value;
        }
      }
    }

    let delay = this.initialDelay;

    this.blocks.forEach((block) => {
      block.style.transitionDelay = `${delay}s`;

      delay += this.delayIncrement;
    });

    const handleIntersection = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.classList.add("visible");
        }
      });
    }

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    });

    observer.observe(this);
  }
}

if (!window.customElements.get("animated-media")) {
  customElements.define("animated-media", AnimatedMedia);
}


class ImageParallax extends AnimatedMedia {
  constructor() {
    super();

    this.image = this.querySelector("img, .placeholder-svg");
    this.parallax = 0.3;
  }

  connectedCallback() {
    super.connectedCallback();

    this.initParallax();
  }

  initParallax() {
    if (!this.image) {
      return;
    }

    const scale = 1 + this.parallax;
    const translate = (this.parallax * 100) / (1 + this.parallax);

    const animationOptions = {
      easing: "linear"
    };

    const scrollOptions = {
      target: this,
      offset: ["start end", "end start"]
    };

    let animation = Motion.animate(this.image, {
      transform: [
        `scale(${scale}) translateY(0)`,
        `scale(${scale}) translateY(${translate}%)`
      ],
      transformOrigin: ["bottom", "bottom"]
    }, animationOptions);

    Motion.scroll(animation, scrollOptions);
  }
}

if (!window.customElements.get("image-parallax")) {
  customElements.define("image-parallax", ImageParallax);
}


class CustomCursor extends HTMLElement {
  constructor() {
    super();

    this.mouseX = 0;
    this.mouseY = 0;

    this.cursorX = 0;
    this.cursorY = 0;

    this.mouseMoveListener = theme.helpers.throttle(this.handleMouseMove.bind(this));
  }

  connectedCallback() {
    if (!theme.params.isTouchDevice) {
      document.addEventListener("mousemove", this.mouseMoveListener, { passive: true });
    }
  }

  disconnectedCallback() {
    if (!theme.params.isTouchDevice) {
      document.removeEventListener("mousemove", this.mouseMoveListener);
    }
  }

  handleMouseMove(evt) {
    this.mouseX = evt.clientX;
    this.mouseY = evt.clientY;

    requestAnimationFrame(() => {
      this.cursorX += (this.mouseX - this.cursorX) / 4;
      this.cursorY += (this.mouseY - this.cursorY) / 4;

      this.style.setProperty("--x", `${this.cursorX}px`);
      this.style.setProperty("--y", `${this.cursorY}px`);
    });
  }
}

if (!window.customElements.get("custom-cursor")) {
  customElements.define("custom-cursor", CustomCursor);
}


class CustomCursorArea extends HTMLElement {
  constructor() {
    super();

    this.cursorName = this.dataset.cursorName;
    this.cursorCustomClass = this.dataset.cursorCustomClass;
    this.customCursor = document.querySelector(`.custom-cursor--${this.cursorName}`);

    this.handleMouseEnterListener = theme.helpers.throttle(this.handleMouseEnter.bind(this));
    this.handleMouseLeaveListener = theme.helpers.throttle(this.handleMouseLeave.bind(this));
  }

  connectedCallback() {
    if (this.customCursor && !theme.params.isTouchDevice) {
      this.addEventListener("mouseenter", this.handleMouseEnterListener, { passive: true });
      this.addEventListener("mouseleave", this.handleMouseLeaveListener, { passive: true });
    }
  }

  disconnectedCallback() {
    if (this.customCursor && !theme.params.isTouchDevice) {
      this.removeEventListener("mouseenter", this.handleMouseEnterListener);
      this.removeEventListener("mouseleave", this.handleMouseLeaveListener);
    }
  }

  handleMouseEnter() {
    this.customCursor.classList.add("active");

    if (this.cursorCustomClass) {
      this.customCursor.classList.add(this.cursorCustomClass);
    }
  }

  handleMouseLeave() {
    this.customCursor.classList.remove("active");

    if (this.cursorCustomClass) {
      this.customCursor.classList.remove(this.cursorCustomClass);
    }
  }
}

if (!window.customElements.get("custom-cursor-area")) {
  customElements.define("custom-cursor-area", CustomCursorArea);
}


class OverlayComponent extends CustomCursorArea {
  constructor() {
    super();

    this.customCursor = document.querySelector(".custom-cursor--close");
  }
}

if (!window.customElements.get("overlay-component")) {
  customElements.define("overlay-component", OverlayComponent);
}


class CircleText extends HTMLElement {
  constructor() {
    super();

    this.trigSupported = CSS.supports("(top: calc(sin(1) * 1px))");
    this.text = this.dataset.text || "Featured • product • ";
    this.originalText = this.dataset.originalText || "Featured product";

    this.renderLetters();
  }

  renderLetters() {
    const charsCount = this.children.length;
    const anglePerChar = `calc((360 / var(--char-total, ${charsCount})) * 1deg)`;
    const radius = `calc((1 / ${this.trigSupported ? "sin(var(--angle-per-char))" : Math.sin(360 / charsCount / (180 / Math.PI))}) * -1ch)`;

    this.style.setProperty("--char-total", charsCount);
    this.style.setProperty("--angle-per-char", anglePerChar);
    this.style.setProperty("--radius", radius);

    const chars = this.text.split("");

    this.innerHTML = "";

    this.style.setProperty("--char-total", chars.length)

    chars.forEach((char, index) => {
      this.innerHTML += `<span aria-hidden="true" class="circle-text__char" style="--char-index: ${index};">${char}</span>`;
    });

    this.innerHTML += `<span class="visually-hidden">${this.originalText}</span>`;

    const updatedRadius = this.trigSupported ? "calc((var(--character-width) / sin(var(--angle-per-char))) * -1ch" : `calc(1 / ${Math.sin(360 / charsCount / (180 / Math.PI))}) * -1ch)`;

    this.style.setProperty("--radius", updatedRadius);
  }
}

if (!window.customElements.get("circle-text")) {
  customElements.define("circle-text", CircleText);
}


class AccentText extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, () => {
      this.classList.add("animate");
    });
  }
}

if (!window.customElements.get("accent-text")) {
  customElements.define("accent-text", AccentText, { extends: "em" });
}


class HoverButton {
  constructor(buttonElement) {
    this.button = buttonElement;

    this.flair = this.button.querySelector(".button__flair");

    if (this.flair) {
      this.initEvents();
    }
  }

  getXY(e) {
    const rect = this.button.getBoundingClientRect();

    return {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    };
  }

  initEvents() {
    this.button.addEventListener("mouseenter", (e) => {
      const { x, y } = this.getXY(e);

      this.flair.style.transform = `translate(${x}%, ${y}%) scale(1)`;
    });

    this.button.addEventListener("mousemove", (e) => {
      const { x, y } = this.getXY(e);

      this.flair.style.transform = `translate(${x}%, ${y}%) scale(1)`;
    });

    this.button.addEventListener("mouseleave", (e) => {
      const { x, y } = this.getXY(e);

      this.flair.style.transform = `translate(${x}%, ${y}%) scale(0)`;
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  // Update button hover
  function applyButtonHover() {
    document.querySelectorAll(".button-primary, .button-outline, .shopify-payment-button__button--unbranded").forEach((button) => {
      new HoverButton(button);
    });
  }

  applyButtonHover();

  document.addEventListener("hover-button:apply", applyButtonHover);
  document.addEventListener("shopify:section:load", applyButtonHover);
});
// added by benitronica
window.addEventListener('pageshow', function (event) {
  const navType = performance.getEntriesByType("navigation")[0]?.type;

  if (event.persisted || navType === "back_forward") {
    setTimeout(() => {
      window.location.reload();
    }, 0); // oppure 100ms se necessario
  }
});
